import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Icon, Loader } from "rsuite";
import { ColorBadge } from "@legacy-components/ColorBadge/ColorBadge";
import { MAX_AUTO_RETRIES, useDataset } from "../DataSet/DatasetProvider";
import { ColumnDefinition } from "../types";
import { useAuthenticatedFileBlob } from "../utils/useAuthenticatedFileBlob";
import { buildTileLayout, formatTileValue, TileLayout } from "./tileLayout";
import "./DynamicTileView.scss";

const DEFAULT_PAGE_SIZE = 24;

type Row = Record<string, unknown>;

const TileImage = ({ value }: { value: unknown }) => {
  // Same convention as the grid's inline-thumbnail column: a comma-separated
  // list of file ids. A card shows the first one.
  const id = String(value ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)[0];
  const { blobUrl, isLoading } = useAuthenticatedFileBlob(id);

  if (isLoading) return <div className="tile-image tile-image-loading" />;
  if (!blobUrl) {
    return (
      <div className="tile-image tile-image-empty">
        <Icon icon="image" size="2x" />
      </div>
    );
  }
  return <img className="tile-image" src={blobUrl} alt="" />;
};

const Tile = ({
  row,
  layout,
  onOpen,
}: {
  row: Row;
  layout: TileLayout;
  onOpen?: () => void;
}) => (
  <div
    className={`tile${onOpen ? " tile-clickable" : ""}`}
    onClick={onOpen}
    role={onOpen ? "button" : undefined}
    tabIndex={onOpen ? 0 : undefined}
    onKeyDown={(e) => {
      if (onOpen && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onOpen();
      }
    }}
  >
    {layout.image && <TileImage value={row[String(layout.image.field)]} />}

    <div className="tile-body">
      <div className="tile-heading">
        <div className="tile-titles">
          {layout.title && (
            <span className="tile-title">
              {formatTileValue(layout.title, row)}
            </span>
          )}
          {layout.subtitle && (
            <span className="tile-subtitle">
              {formatTileValue(layout.subtitle, row)}
            </span>
          )}
        </div>
        {layout.status && (
          <ColorBadge
            width="110px"
            height="22px"
            fontSize="11px"
            fontWeight="600"
            semanticValue={row.cdSemanticValue as string}
            text={formatTileValue(layout.status, row)}
          />
        )}
      </div>

      <dl className="tile-fields">
        {layout.fields.map((col: ColumnDefinition) => (
          <div className="tile-field" key={String(col.field)}>
            <dt>{col.headerName ?? col.field}</dt>
            <dd>{formatTileValue(col, row)}</dd>
          </div>
        ))}
      </dl>
    </div>
  </div>
);

const DynamicTileView = () => {
  const {
    columnDefinition,
    fetchRows,
    filtersVersion,
    hasError,
    traceId,
    retryCountdown,
    retryAttempt,
    settings,
  } = useDataset();

  const history = useHistory();
  const location = useLocation();

  const pageSize = settings?.tileView?.pageSize ?? DEFAULT_PAGE_SIZE;
  const layout = useMemo(
    () => buildTileLayout(columnDefinition, settings?.tileView),
    [columnDefinition, settings?.tileView],
  );

  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [isFetching, setIsFetching] = useState(true);

  // fetchRows changes identity whenever the debounced filters change, and
  // filtersVersion already tracks that. Reading it from a ref keeps the paging
  // effect from firing twice for a single filter change.
  const fetchRowsRef = useRef(fetchRows);
  useEffect(() => {
    fetchRowsRef.current = fetchRows;
  }, [fetchRows]);

  const loadPage = useCallback(
    async (startRow: number) => {
      setIsFetching(true);
      const res = await fetchRowsRef.current({
        startRow,
        endRow: startRow + pageSize,
      });
      if (!res.canceled) {
        const page = (res.rows ?? []) as Row[];
        setTotal(res.total ?? 0);
        setRows((prev) => (startRow === 0 ? page : [...prev, ...page]));
      }
      setIsFetching(false);
    },
    [pageSize],
  );

  useEffect(() => {
    setRows([]);
    setTotal(0);
    void loadPage(0);
  }, [filtersVersion, loadPage]);

  const openRow = (row: Row) => {
    if (!layout.idColumn?.field) return;
    const id = row[String(layout.idColumn.field)];
    if (id === null || id === undefined) return;
    const basePath = layout.path
      ? `/${layout.path.replace(/^\/|\/$/g, "")}`
      : location.pathname.replace(/\/$/, "");
    history.push(`${basePath}/${id}`);
  };

  if (hasError) {
    return (
      <div className="DynamicTileView">
        <div className="errorDisplay">
          {retryCountdown > 0 ? (
            <>
              <div>The data could not be loaded.</div>
              <div>
                {"Retrying in "}
                <strong>{retryCountdown}s</strong>...
              </div>
            </>
          ) : retryAttempt > MAX_AUTO_RETRIES ? (
            <>
              The data could not be loaded after multiple attempts. Please try
              again later.
            </>
          ) : null}
          <br />
          {traceId ? `TraceID: ${traceId}` : ""}
        </div>
      </div>
    );
  }

  return (
    <div className="DynamicTileView">
      {rows.length === 0 && isFetching && (
        <div className="tile-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="tile tile-skeleton" key={i} />
          ))}
        </div>
      )}

      {rows.length === 0 && !isFetching && (
        <div className="tile-empty">No items to display</div>
      )}

      {rows.length > 0 && (
        <>
          <div className="tile-grid">
            {rows.map((row, index) => (
              <Tile
                key={
                  layout.idColumn?.field
                    ? String(row[String(layout.idColumn.field)] ?? index)
                    : index
                }
                row={row}
                layout={layout}
                onOpen={
                  layout.idColumn?.field ? () => openRow(row) : undefined
                }
              />
            ))}
          </div>

          {rows.length < total && (
            <div className="tile-footer">
              {isFetching ? (
                <Loader size="sm" content="Loading..." />
              ) : (
                <button
                  className="tile-load-more"
                  onClick={() => void loadPage(rows.length)}
                >
                  Load more ({rows.length} of {total})
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DynamicTileView;
